
import type { OXQuestion, OXResult } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

export interface OXActions {
  onAnswer?: (token: number, answer: boolean) => void;
}

export const OX_CORRECT = 0;
export const OX_WRONG = 1;
export const OX_UNANSWERED = 2;
export const OX_AUDIENCE = 3;

/** `OXEvent.cpp:300,332`'s server-selected `SE_FAIL` / `SE_SUCCESS` intent. */
export const OX_EFFECT_NONE = 0;
export const OX_EFFECT_SUCCESS = 1;
export const OX_EFFECT_FAIL = 2;

export function describeOXResult(result: OXResult): string {
  // The reveal's own words (`OXEvent.cpp:210,215`: `SendNoticeMap(LC_TEXT("Yes (O)")`
  // / `LC_TEXT("No (X)")`), not an invented "True"/"False" gloss.
  const answer = result.correct_answer ? "Yes (O)" : "No (X)";
  if (result.outcome === OX_CORRECT) return `Correct — the answer was ${answer}.`;
  if (result.outcome === OX_WRONG) return `Wrong — the answer was ${answer}.`;
  if (result.outcome === OX_UNANSWERED) return `No answer — it was ${answer}.`;
  return `The answer was ${answer}. ${result.survivors} competitors remain.`;
}

const STYLE_ID = "oxevent-style";
const STYLE = `
.oxevent { position:fixed; left:50%; top:15%; transform:translateX(-50%); z-index:64;
  width:min(520px,calc(100vw - 32px)); padding:14px 16px; border-radius:12px;
  background:rgba(10,12,18,.95); border:1px solid rgba(245,197,92,.5);
  color:#edf1f7; font:14px/1.45 system-ui,sans-serif; text-align:center; pointer-events:auto; }
.oxevent[hidden] { display:none; }
.oxevent-title { color:#f5c55c; font-size:11px; letter-spacing:.14em; text-transform:uppercase; }
.oxevent-question { margin:10px 0 12px; font-size:17px; }
.oxevent-buttons { display:flex; gap:10px; }
.oxevent-button { flex:1; padding:9px; border-radius:8px; border:1px solid rgba(255,255,255,.3);
  background:rgba(255,255,255,.08); color:#fff; cursor:pointer; font:inherit; }
.oxevent-button:hover { background:rgba(245,197,92,.18); }
.oxevent-button[aria-pressed="true"] { border-color:#f5c55c; background:rgba(245,197,92,.28); }
.oxevent-result { margin-top:9px; min-height:1.45em; color:#d9e3ef; }
.oxevent[data-oxeffect="success"] { border-color:rgba(98,214,128,.8); }
.oxevent[data-oxeffect="fail"] { border-color:rgba(238,104,104,.8); }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export class OXEventPanel {
  private readonly root: HTMLElement;
  private readonly statement: HTMLElement;
  private readonly result: HTMLElement;
  private readonly o: HTMLButtonElement;
  private readonly x: HTMLButtonElement;
  private question: OXQuestion | null = null;

  constructor(container: HTMLElement, private readonly actions: OXActions = {}) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.root = elem(doc, "section", "oxevent");
    this.root.hidden = true;
    this.root.dataset.oxevent = "closed";
    this.root.dataset.oxeffect = "none";

    const title = elem(doc, "div", "oxevent-title");
    // GameForge's own name for this NPC event is "OX contest", never "OX
    // Quiz" (`gameforge.oxevent`, every `_*_say`/`_*npcChat` entry).
    title.textContent = "OX Contest";
    this.statement = elem(doc, "div", "oxevent-question");
    const buttons = elem(doc, "div", "oxevent-buttons");
    // Same parenthetical the server's own reveal uses (`OXEvent.cpp:210,215`),
    // not a house "True"/"False" gloss.
    this.o = this.button(doc, "Yes (O)", true);
    this.x = this.button(doc, "No (X)", false);
    buttons.append(this.o, this.x);
    this.result = elem(doc, "div", "oxevent-result");
    this.root.append(title, this.statement, buttons, this.result);
    container.append(this.root);
  }

  private button(doc: Document, label: string, answer: boolean): HTMLButtonElement {
    const button = doc.createElement("button");
    button.type = "button";
    button.className = "oxevent-button";
    button.textContent = label;
    button.setAttribute("aria-pressed", "false");
    button.addEventListener("click", () => this.answer(answer));
    return button;
  }

  update(question: OXQuestion): void {
    this.question = question;
    this.root.hidden = false;
    this.root.dataset.oxevent = "question";
    this.root.dataset.oxeffect = "none";
    this.root.dataset.token = String(question.token);
    this.statement.textContent = question.text;
    // `OXEvent.cpp:245`'s own instruction, verbatim.
    this.result.textContent = "If it's correct, then go to O. If it's wrong, go to X.";
    this.o.disabled = false;
    this.x.disabled = false;
    this.o.setAttribute("aria-pressed", "false");
    this.x.setAttribute("aria-pressed", "false");
  }

  showResult(result: OXResult): void {
    if (this.question !== null && result.token !== this.question.token) return;
    this.question = null;
    this.root.hidden = false;
    this.root.dataset.oxevent = "result";
    this.root.dataset.oxeffect = result.effect === OX_EFFECT_SUCCESS
      ? "success"
      : result.effect === OX_EFFECT_FAIL
        ? "fail"
        : "none";
    this.statement.textContent = `${result.survivors} competitors remain.`;
    this.result.textContent = describeOXResult(result);
    this.o.disabled = true;
    this.x.disabled = true;
  }

  private answer(answer: boolean): void {
    const question = this.question;
    if (question === null) return;
    this.o.setAttribute("aria-pressed", String(answer));
    this.x.setAttribute("aria-pressed", String(!answer));
    this.result.textContent = answer ? "Yes (O) selected." : "No (X) selected.";
    this.actions.onAnswer?.(question.token, answer);
  }
}
